import React, { useEffect, useState, useMemo, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import "../../../../features/campaign/campiagn-view/campaign-view-details/campaign-view-details-view/campaign-view-details-view-leads-info/add-new-campaign/add-new-campiagn.scss";
import { IconButton } from "@mui/material";
import _ from "lodash";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { getLeadCaptureForms } from "../../../../api/lead-capture";
import {
  AlertComponent,
  PaginationComponent,
  SearchInputComponent,
} from "../../../../common/components";
import LeadCaptureTable from "../../../../common/components/modal-table/lead-capture-form-select-modal-tabel";

interface AddToCampaignModalProps {
  isOpen: boolean;
  setIsOpen: any;
  multipleCampaign?: boolean;
  onSave?: () => void;
  selectedLeadCaptureForm?: {
    id: number;
    formName: string;
    formLink: string;
  } | null;
  setSelectedLeadCaptureForm?: any;
  ourServiceData?: any;
}

const SelectLeadCaptureFormModal: React.FC<AddToCampaignModalProps> = ({
  isOpen,
  setIsOpen,
  multipleCampaign,
  selectedLeadCaptureForm,
  setSelectedLeadCaptureForm,
  onSave,
  ourServiceData,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [pageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0); // Set an initial value for testing
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isResetSearch, setIsResetSearch] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const isSearchTriggered = useRef(false);

  const handleSearch = _.debounce((text: string) => {
    isSearchTriggered.current = true;
    setCurrentPage(1);
    setSearchText(text);
  }, 500);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const fetchLeadCaptureForms = async (page = 1, search: string = "") => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (search) {
        params.searchText = search;
      }

      const response = await getLeadCaptureForms(params);
      setData(response.data.data.data);
      setTotalRecords(response.data.data.total);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch lead capture forms."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  const handleSave = async () => {
    if (!selectedLeadCaptureForm?.id) {
      setAlertMessage("Please select lead capture form.");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }
    try {
      setAlertMessage("Lead capture form inserted successfully.");
      setAlertType("success");
      setCurrentPage(1);
      setAlertOpen(true);
      setLoading(false);
      onSave?.();
    } catch (err: any) {
      setLoading(false);
      setAlertMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to assign customers to campaign."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setIsOpen(false);
      setSearchText("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchLeadCaptureForms(pageToUse, searchText);
      isSearchTriggered.current = false;
    }
  }, [isOpen, currentPage, searchText]);

  const handleCancel = () => {
    setSelectedLeadCaptureForm({...ourServiceData?.leadCaptureFormData});
    setSearchText("");
    setIsOpen(false);
    setLoading(false);
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);
  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading LeadCapture Forms...";
    }
    if (searchText) {
      return "No lead forms found matching your search";
    }
    return "No lead capture forms available, Please create a form by clicking on Create New Form";
  }, [isLoading, searchText]);

  return (
    <div className="mass-update-wrapper">
      <Dialog
        open={isOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCancel();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="update-popup-wrapper"
        slotProps={{
          paper: {
            sx: {
              minWidth: "1000px",
              minHeight: "600px",
              borderBottomLeftRadius: "30px",
              borderBottomRightRadius: "30px",
              top: 0,
              margin: 0,
              position: "absolute",
            },
          },
        }}
      >
        <DialogTitle className="campaign-modal__header">
          <span>Lead Capture Forms</span>
          <IconButton onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="campaign-modal__content">
          <div className="campaign-modal__search-filter-wrapper">
            <div className="campaign-modal__search-filter">
              <SearchInputComponent
                placeholder="Search by Form Name, Created By"
                onSearch={handleSearch}
                isReset={isResetSearch}
              />
            </div>
            {Number(selectedLeadCaptureForm?.id) > 0 && (
              <p>
                {" "}
                <strong>Selected Form</strong> :{" "}
                {selectedLeadCaptureForm?.formName}
              </p>
            )}
          </div>

          <LeadCaptureTable
            data={data}
            selectedLeadCaptureForm={selectedLeadCaptureForm}
            setSelectedLeadCaptureForm={setSelectedLeadCaptureForm}
            multiple={multipleCampaign}
            noDataMessage={noResultsTxt}
          />

          <div className="campaign-modal__footer">
            {totalRecords && totalRecords > 0 ? (
              <div className="leads-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}

            <div className="campaign-modal__action-buttons" style={{marginLeft: "auto"}}>
              <button onClick={handleCancel} className="cancel-button">
                Cancel
              </button>
              <button
                className="cancel-button next-button-wrapper"
                onClick={handleSave}
              >
                {"Insert"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </div>
  );
};

export default SelectLeadCaptureFormModal;
